/**
 * useAppOptimization Hook
 *
 * Handles DC optimization submission and status polling.
 * Follows the standard Vortx pattern: submit -> poll results -> done.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { fetchDC } from '../services/api';

/**
 * Hook for running DC optimizations
 *
 * @param {string} projectId - Project ID
 * @returns {Object} { submit, status, results, loading, error, cancel }
 */
export function useAppOptimization(projectId) {
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const pollingInterval = useRef(null);

  const submit = useCallback(
    async (dcScenario = {}) => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      try {
        setLoading(true);
        setError(null);
        setResults(null);
        setStatus('submitting');

        const response = await fetchDC('/optimize', {
          method: 'POST',
          body: JSON.stringify({
            project_id: projectId,
            ...dcScenario,
          }),
        });

        const newJobId = response.job_id;
        setJobId(newJobId);
        setStatus('running');

        return newJobId;
      } catch (err) {
        console.error('Failed to submit DC optimization:', err);
        setError(err.message || 'Failed to submit optimization');
        setStatus('failed');
        setLoading(false);
        throw err;
      }
    },
    [projectId]
  );

  const pollResults = useCallback(async () => {
    if (!jobId) return;

    try {
      const response = await fetchDC(`/results/${jobId}`);
      const newStatus = response.status;

      setStatus(newStatus);

      if (newStatus === 'completed') {
        setResults(response.result);
        setLoading(false);

        if (pollingInterval.current) {
          clearInterval(pollingInterval.current);
          pollingInterval.current = null;
        }
      } else if (newStatus === 'failed') {
        setError(response.error || 'Optimization failed');
        setLoading(false);

        if (pollingInterval.current) {
          clearInterval(pollingInterval.current);
          pollingInterval.current = null;
        }
      }
    } catch (err) {
      console.error('Failed to poll DC optimization results:', err);
      setError(err.message || 'Failed to check optimization status');
      setLoading(false);

      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
    }
  }, [jobId]);

  useEffect(() => {
    if (jobId && !pollingInterval.current) {
      pollingInterval.current = setInterval(pollResults, 2000);
    }

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
    };
  }, [jobId, pollResults]);

  const cancel = useCallback(() => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
    setJobId(null);
    setStatus(null);
    setLoading(false);
  }, []);

  return {
    submit,
    status,
    results,
    loading,
    error,
    cancel,
  };
}

export default useAppOptimization;
